import { Tooltip, Typography } from "antd-v5";
import classNames from "classnames";
import AuthorisedContainer from "components/AuthorisedContainer";
import EditIconButton from "components/EditIconButton";
import { useFeatureFlag } from "contexts/FeatureFlagContext";
import { useGlobalSettingsContext } from "contexts/GlobalSettingsContext";
import dayjs from "dayjs";
import _ from "lodash";
import React, { useMemo, useState } from "react";
import { CellRenderers } from "utils/tableUtils";
import { renderLastResolutionNote } from "views/Findings/utils";

import { NOT_AVAILABLE } from "ac-components/ac-na-component/ac-na-component";
import ACSegmented from "ac-components/ac-segmented/ACSegmented";

import "./ACTimeline.less";

type TimeLineFieldPropsType = {
  event: string | React.ReactNode;
  date: number;
  dateType: string;
  icon?: (e: any) => React.ReactNode;
  editAction?: (e: any) => void;
  key: string;
};

type FindingFieldPropsType = {
  label: string | React.ReactNode;
  path: string;
  dateType: "other" | "sla";
  icon?: (e: any) => React.ReactNode;
  editAction?: (e: any) => void;
  actionPermission?: string;
};

export default function FindingTimeline({ finding, fields }: any) {
  const [segmentValue, setSegmentValue] = useState("all");
  const [fieldWithDate, fieldWithoutDate] = useMemo(() => {
    const _fields: TimeLineFieldPropsType[] = fields.map(
      ({
        label,
        path,
        dateType,
        icon,
        editAction,
        actionPermission,
      }: FindingFieldPropsType) => {
        const value = _.get(finding, path.split("."));

        return {
          event: _.isFunction(label) ? label() : label,
          date: value,
          icon: icon ? icon(finding) : null,
          dateType,
          editAction:
            editAction && actionPermission ? (
              <AuthorisedContainer permission={actionPermission}>
                {
                  // @ts-expect-error
                  <EditIconButton onClick={editAction} className="m-0" />
                }
              </AuthorisedContainer>
            ) : null,
          key: path,
        };
      },
    );
    return [
      _fields.filter(({ date }) => date),
      _fields.filter(({ date }) => _.isNil(date)),
    ];
  }, [finding, fields]);

  const finalData = useMemo(() => {
    let filteredData = fieldWithDate;
    if (segmentValue !== "all") {
      filteredData = _.filter(
        fieldWithDate,
        ({ dateType }) => dateType === segmentValue,
      );
    }
    return _.sortBy(filteredData, "date");
  }, [fieldWithDate, segmentValue]);

  return (
    <div className="finding-timeline">
      <ACSegmented
        className="w-100"
        items={[
          { label: "All", value: "all" },
          { label: "SLA", value: "sla" },
          { label: "Other", value: "other" },
        ]}
        onChange={(value) => setSegmentValue(value as string)}
        value={segmentValue}
      />
      {!_.isEmpty(finalData) ? (
        <ACTimeLine timeline={finalData} finding={finding} />
      ) : (
        <Typography.Paragraph className="text-center m-b-0" type="secondary">
          No Timeline
        </Typography.Paragraph>
      )}
      {!_.isEmpty(fieldWithoutDate) ? (
        <div className="no-dates-available">
          No dates available for:&nbsp;{" "}
          {fieldWithoutDate.map(({ event }, i) => {
            return (
              <React.Fragment key={i}>
                {event} {i < fieldWithoutDate.length - 1 ? <>,&nbsp;</> : ""}
              </React.Fragment>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function ACTimeLine({
  timeline,
  finding,
}: {
  timeline: TimeLineFieldPropsType[];
  finding: any;
}) {
  const globalSettings = useGlobalSettingsContext();
  const featureFlags = useFeatureFlag();
  const currentDate = dayjs().valueOf();
  const lastOnDateIndex = _.findLastIndex(
    timeline,
    (item: any) =>
      CellRenderers.DATE(item.date) === CellRenderers.DATE(currentDate),
  );
  const nearToOnDateIndex = _.findLastIndex(
    timeline,
    (item: any) =>
      CellRenderers.DATE(item.date) < CellRenderers.DATE(currentDate),
  );

  return (
    <div className="ac-timeline">
      {_.map(timeline, (item, i) => {
        const onDate = lastOnDateIndex === +i;
        const nearToOnDate = nearToOnDateIndex === +i;
        const isResolutionDueDate = item.key === "slaDueDate";
        let dateValue = CellRenderers.DATE(item.date);
        let icon = item.icon;
        let editAction = item.editAction;
        if (item.dateType === "sla" && !globalSettings.isSLAEnabled) {
          dateValue = NOT_AVAILABLE;
          icon = undefined;
          editAction = undefined;
        }
        return (
          <div
            key={i}
            className={classNames("ac-timeline-event-wrapper", {
              "has-due": item.date > currentDate,
              "near-to-on-date": lastOnDateIndex === -1 && nearToOnDate,
              "last-on-date": onDate,
            })}
          >
            <div className="ac-timeline-event">
              <div className="ac-timeline-event-name">{item.event}</div>
              <div className="ac-timeline-event-date">
                <Tooltip
                  title={
                    isResolutionDueDate ? (
                      <>
                        Changed:{" "}
                        {renderLastResolutionNote(finding?.lastResolutionNote)}
                      </>
                    ) : undefined
                  }
                >
                  <span>{dateValue}</span>
                </Tooltip>
                {icon}
                {editAction}
              </div>
            </div>
            <div className="ac-timeline-indicator">
              <div className="dot" />
              <div className="line" />
            </div>
            <div className="ac-timeline-event" />
          </div>
        );
      })}
    </div>
  );
}
